export const LANDING_PAGE_CONTENT = {
  seo: {
    title: "Illinois Address Center | Business Address & Digital Mail Management Near Chicago",
    description:
      "Get a professional physical mailing address in Summit, Illinois serving the Greater Chicago area. Receive mail and packages, view arrivals online, request scans, and book meeting space when needed.",
  },
  brand: {
    name: "Illinois Address Center",
    initials: "IA",
    tagline: "Your Business Address. Managed Online.",
    location: "Located in Summit, IL. Serving the Greater Chicago area.",
  },
  nav: {
    links: [
      { label: "Home", href: "#home" },
      { label: "Services", href: "#services" },
      { label: "How It Works", href: "#how-it-works" },
      { label: "About", href: "#about" },
      { label: "FAQ", href: "#faq" },
    ],
    loginLabel: "Login",
    registerLabel: "Register / Get Started",
  },
  hero: {
    eyebrow: "Located in Summit, Illinois",
    headline: "A Professional Business Address in the Greater Chicago Area",
    subheadline:
      "Illinois Address Center gives businesses, entrepreneurs, remote workers, and individuals a real physical mailing address with digital mail management, package receiving, optional scanning, and on-demand meeting space.",
    primaryCta: "Get Started",
    secondaryCta: "See How It Works",
    bullets: [
      "Real street-address format with private unit designation",
      "Receive mail and packages from major carriers",
      "View incoming mail through your tenant portal",
      "Request secure scans and uploads when needed",
      "Book conference space in the same building",
    ],
    visual: {
      title: "Illinois Address Center Portal",
      status: "New Mail Received",
      badge: "Live",
      actionsTitle: "Quick actions",
      items: [
        { sender: "First National Bank", type: "Letter", status: "Scan Available" },
        { sender: "Amazon", type: "Package", status: "Ready for Pickup" },
        { sender: "State Agency", type: "Document", status: "Uploaded" },
      ],
      actions: ["Request Scan", "Forward Mail", "Schedule Pickup"],
    },
  },
  clarification: {
    headline: "Not a PO Box. Not just mail forwarding.",
    copy:
      "Illinois Address Center provides a professional physical mailing address located in Summit, IL, serving the Greater Chicago area.",
    cards: [
      {
        title: "Physical Address Format",
        copy: "Use a street-address style format with your assigned unit designation.",
      },
      {
        title: "Digital Mail Visibility",
        copy: "See what arrives and manage requests through your online tenant portal.",
      },
      {
        title: "Professional Support",
        copy: "Package receiving, mail scanning, forwarding, pickup, and meeting room access.",
      },
    ],
  },
  services: {
    id: "services",
    eyebrow: "Services",
    heading: "Address, Mail, Packages, and Workspace - All in One Place",
    subheading:
      "Illinois Address Center combines professional address services with modern digital mail tools and in-person meeting options.",
    cards: [
      {
        title: "Physical Business Address",
        copy:
          "Customers receive a professional street-address format with a private unit designation at our Summit, Illinois location.",
      },
      {
        title: "Mail Receiving",
        copy: "We receive and securely log incoming mail so customers know what arrived and when.",
      },
      {
        title: "Package Receiving",
        copy:
          "Accept packages from USPS, UPS, FedEx, DHL, Amazon, and other major carriers, subject to service rules and package limits.",
      },
      {
        title: "Digital Tenant Portal",
        copy: "Tenants can log in to view received mail, track item status, and request actions from their dashboard.",
      },
      {
        title: "Scan & Upload Requests",
        copy:
          "Upon tenant request and authorization, eligible mail can be opened, scanned, and uploaded securely to the user portal.",
      },
      {
        title: "Conference Room Booking",
        copy:
          "Tenants may book in-person conference space in the same building for client meetings, consultations, or presentations.",
      },
    ],
  },
  howItWorks: {
    id: "how-it-works",
    eyebrow: "Process",
    heading: "How It Works",
    steps: [
      {
        title: "Choose Your Plan",
        copy: "Select the address and mail-management plan that fits your personal or business needs.",
      },
      {
        title: "Complete Verification",
        copy: "Complete required identity verification and authorization documents before mail handling begins.",
      },
      {
        title: "Start Receiving Mail",
        copy: "Use your assigned address format to receive business mail, personal mail, and packages where permitted.",
      },
      {
        title: "Manage Everything Online",
        copy: "Log into your tenant portal to see what arrived, request scans, arrange pickup, or manage forwarding.",
      },
    ],
  },
  portal: {
    eyebrow: "Tenant portal",
    heading: "Your Mail, Visible From Anywhere",
    copy:
      "With our tenant portal, you do not have to wonder whether important mail has arrived. Incoming items are logged into your account so you can view status updates and request the next action.",
    features: [
      "View incoming mail and packages",
      "See arrival dates and item status",
      "Request mail scanning",
      "Access uploaded scanned documents",
      "Request pickup or forwarding",
      "Keep business correspondence organized",
    ],
    tableHeaders: ["Date", "Type", "Sender", "Status", "Action"],
    rows: [
      { date: "Jun 12", type: "Letter", sender: "Bank", status: "Received", action: "Request Scan" },
      { date: "Jun 13", type: "Package", sender: "Amazon", status: "Ready for Pickup", action: "Schedule Pickup" },
      { date: "Jun 14", type: "Document", sender: "State Agency", status: "Scanned", action: "View PDF" },
    ],
  },
  useCases: {
    eyebrow: "Possible uses",
    heading: "What Can You Use Illinois Address Center For?",
    intro:
      "Many customers use Illinois Address Center as a professional mailing address for business operations, remote work, privacy, and mail organization. Acceptance depends on the rules of the specific agency, bank, platform, or organization requesting the address.",
    cards: [
      {
        title: "Business Mailing Address",
        copy: "Use the address for general business correspondence, vendor mail, and customer-facing contact information.",
      },
      {
        title: "Startup / LLC Correspondence",
        copy: "Use the address for business mail and official correspondence where accepted by the relevant registration authority.",
      },
      {
        title: "Website & Marketing Contact Address",
        copy: "Display a professional mailing address on your website, invoices, business cards, and marketing materials.",
      },
      {
        title: "Privacy Protection",
        copy: "Keep your home address private by using a professional address for business mail and public-facing contact needs.",
      },
      {
        title: "Remote Business Operations",
        copy: "Operate from home, on the road, or from another state while still maintaining a reliable Illinois mailing point.",
      },
      {
        title: "Package & Document Receiving",
        copy: "Receive important documents and packages at a professional location rather than at your home.",
      },
    ],
    disclaimer:
      "Address acceptance varies by use case. Customers are responsible for confirming whether the address is accepted by their bank, licensing agency, state agency, marketplace, or business registration authority. Illinois Address Center does not provide legal, tax, or licensing advice.",
  },
  conferenceRoom: {
    eyebrow: "Meeting space",
    heading: "Need to Meet in Person? Book Conference Space When You Need It.",
    copy:
      "For customers who need more than an address, Illinois Address Center offers access to conference space in the same building by reservation. Meet clients, host consultations, review documents, or present your business in a professional environment without leasing a full office.",
    bullets: [
      "Available by reservation",
      "Ideal for client meetings and consultations",
      "Professional setting in Summit, IL",
      "Useful for remote businesses needing occasional in-person space",
      "Offered on an as-needed basis",
    ],
    cta: "Ask About Conference Room Availability",
  },
  about: {
    id: "about",
    eyebrow: "About",
    heading: "About Illinois Address Center",
    paragraphs: [
      "Illinois Address Center is a professional address and digital mail management service located in Summit, Illinois, serving businesses, entrepreneurs, remote professionals, and individuals throughout the Greater Chicago area.",
      "We help customers receive mail and packages at a real physical location while giving them the convenience of online mail visibility. Instead of guessing whether something arrived, tenants can log into their portal, view received items, and request services such as scanning, pickup, or forwarding.",
      "Our goal is to give customers the benefits of a professional address, secure mail handling, digital access, and occasional meeting space without the cost or commitment of a traditional office lease.",
      "Whether you are starting a business, protecting your home address, working remotely, traveling often, or organizing business mail, Illinois Address Center gives you a practical address solution backed by modern tools.",
    ],
    locationStatement: "Located in Summit, Illinois. Serving the Greater Chicago area.",
  },
  faq: {
    id: "faq",
    eyebrow: "FAQ",
    heading: "Frequently Asked Questions",
    items: [
      {
        question: "Is this a PO Box?",
        answer:
          "No. Illinois Address Center provides a physical street-address format with a private unit designation at our Summit, Illinois location. It is different from a USPS PO Box.",
      },
      {
        question: "Where is the address located?",
        answer: "The address is located in Summit, Illinois. We serve customers throughout the Greater Chicago area.",
      },
      {
        question: "Can I advertise this as a Chicago address?",
        answer:
          "The physical address is in Summit, Illinois, not Chicago city limits. We recommend describing it as a Greater Chicago area address or a professional address near Chicago.",
      },
      {
        question: "Can I receive packages?",
        answer:
          "Yes. We can receive packages from major carriers such as USPS, UPS, FedEx, DHL, Amazon, and others, subject to package size, storage, carrier, and plan limitations.",
      },
      {
        question: "How will I know when mail arrives?",
        answer: "Incoming mail and packages are logged into your tenant account. You can view received items through your online portal.",
      },
      {
        question: "Can you open and scan my mail?",
        answer:
          "Yes, when requested and authorized by the tenant. Eligible mail can be opened, scanned, and uploaded securely to the tenant portal.",
      },
      {
        question: "Can I use this address for my business?",
        answer:
          "Many customers use the address for business mail, public-facing contact information, vendor correspondence, and other business-related mail needs. Acceptance depends on the organization, agency, bank, or platform requesting the address.",
      },
      {
        question: "Can I use this address to register an LLC or business?",
        answer:
          "It may be usable for certain business registration or correspondence purposes where accepted. Customers should confirm requirements with the Illinois Secretary of State, their attorney, accountant, bank, licensing agency, or other relevant authority before relying on the address for a specific legal or regulatory purpose.",
      },
      {
        question: "Can I use this address for my driver's license or state ID?",
        answer:
          "Address requirements for IDs and government documents vary. Customers should verify directly with the relevant government agency before using the address for identification or residency-related purposes.",
      },
      {
        question: "Do I need to complete verification paperwork?",
        answer:
          "Yes. Identity verification and authorization documents may be required before we can receive, open, scan, forward, or otherwise manage mail on your behalf.",
      },
      {
        question: "Can I pick up mail in person?",
        answer:
          "Yes. Tenants may pick up mail and packages in person during available pickup hours, subject to account status and verification.",
      },
      {
        question: "Do you forward mail?",
        answer:
          "Yes. Mail forwarding may be available depending on the tenant's plan and request. Additional postage, handling, or service fees may apply.",
      },
      {
        question: "Can I book conference space?",
        answer: "Yes. Conference room access is available by reservation on an as-needed basis in the same building.",
      },
      {
        question: "Who is this service for?",
        answer:
          "Illinois Address Center is designed for entrepreneurs, small businesses, remote workers, consultants, freelancers, frequent travelers, privacy-conscious individuals, and anyone needing a professional mailing address with digital management tools.",
      },
    ],
  },
  finalCta: {
    heading: "Ready for a Professional Address You Can Manage Online?",
    copy:
      "Get a physical mailing address in Summit, Illinois with digital mail visibility, package receiving, optional scanning, and access to meeting space when you need it.",
    primaryCta: "Create Your Account",
    secondaryCta: "Contact Us",
  },
  footer: {
    columns: [
      {
        heading: "Services",
        links: ["Business Address", "Mail Receiving", "Package Receiving", "Digital Mail Portal", "Mail Scanning", "Conference Room Booking"],
      },
      {
        heading: "Company",
        links: ["About", "FAQ", "Contact", "Login", "Register"],
      },
      {
        heading: "Legal / Compliance",
        links: ["Terms of Service", "Privacy Policy", "Mail Handling Policy", "Verification Requirements"],
      },
    ],
    disclaimer:
      "Illinois Address Center provides address, mail receiving, digital mail management, and workspace-related services. Address acceptance varies by use case. Customers are responsible for confirming requirements with any agency, bank, licensing authority, business registry, or third party before using the address for a specific purpose. Illinois Address Center does not provide legal, tax, or licensing advice.",
  },
  modal: {
    closeLabel: "Close",
    loginTitle: "Login",
    loginIntro: "Use your account credentials to access the dashboard.",
    registerTitle: "Registration Coming Soon",
    registerIntro:
      "Sorry, registration is not available yet. Account creation is planned for a nearby future update.",
    registerConfirm: "Okay",
  },
};
